import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Link as LinkIcon, Globe, Users, Shield, CheckCircle2, Building2, TrendingUp, Target, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';

const Services = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Alignk" className="h-10" />
            </Link>
            <Link to="/">
              <Button variant="outline" className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Our <span style={{ color: '#4AC5DB' }}>Backlink Services</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Professional link building services to boost your SEO rankings and domain authority
          </p>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <Building2 className="h-6 w-6" style={{ color: '#4AC5DB' }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#4AC5DB' }}>What We Do</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Alignk provides professional backlink building services for businesses looking to improve their search engine rankings. We secure high-quality, do-follow backlinks from authority websites with high Domain Authority (DA) scores.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Our white-hat link building approach ensures that every backlink is placed on real, legitimate websites through editorial content. We never use Private Blog Networks (PBNs), spam, or black-hat techniques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Backlinks */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <LinkIcon className="h-6 w-6" style={{ color: '#4AC5DB' }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#4AC5DB' }}>Quality Backlink Building</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We build high-quality backlinks from authority websites that actually improve your search rankings. Every backlink is manually placed on real websites with genuine traffic and engagement.
              </p>
              <div className="bg-white p-6 rounded-lg border border-gray-200 mb-4">
                <h3 className="font-semibold text-gray-800 mb-3">What You Get:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Permanent do-follow backlinks from high DA websites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Niche-relevant placements for maximum SEO impact</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Detailed reports with live URLs and DA scores</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Natural anchor text distribution</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>White-hat, Google-compliant link building</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <TrendingUp className="h-6 w-6" style={{ color: '#4AC5DB' }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#4AC5DB' }}>SEO Benefits of Backlinks</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Backlinks are one of Google's top ranking factors. When authority websites link to your site, it signals to search engines that your content is valuable and trustworthy.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Improve Rankings</h4>
                  <p className="text-sm text-gray-600">Quality backlinks help your pages rank higher on Google search results.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Increase DA</h4>
                  <p className="text-sm text-gray-600">Build your website's Domain Authority with high-quality inbound links.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Drive Traffic</h4>
                  <p className="text-sm text-gray-600">Get referral visitors from websites linking to your content.</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">Build Authority</h4>
                  <p className="text-sm text-gray-600">Establish your brand as an authority in your industry.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Network */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <Globe className="h-6 w-6" style={{ color: '#4AC5DB' }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#4AC5DB' }}>Our Backlink Network</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We have access to 500+ authority websites across multiple industries and niches. Our network includes news sites, industry blogs, business publications, and niche-specific platforms.
              </p>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-3">Network Includes:</h3>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>News and media websites (DA 40-80+)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Industry blogs and publications</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Business and technology sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                    <span>Niche-specific platforms</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-cyan-100 flex items-center justify-center">
              <Users className="h-6 w-6" style={{ color: '#4AC5DB' }} />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4" style={{ color: '#4AC5DB' }}>Who Buys Our Backlinks?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our backlink services are designed for businesses and professionals serious about improving their SEO:
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  <span><strong>E-commerce Stores:</strong> Online shops wanting to rank higher and drive more organic sales</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Small & Medium Businesses:</strong> Companies looking to improve their Google rankings</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  <span><strong>SEO Agencies:</strong> Agencies managing link building for multiple clients</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Startups:</strong> New businesses building their online presence</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                  <span><strong>Bloggers & Content Creators:</strong> Individuals growing their audience through SEO</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <Shield className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4 text-yellow-700">Important Disclaimer</h2>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Backlinks are one of many factors that influence search engine rankings.</strong>
                </p>
                <p className="text-gray-600 leading-relaxed mb-3">
                  We do NOT guarantee specific ranking improvements, traffic increases, or business outcomes. Results depend on numerous factors including your website quality, content, competition, and search engine algorithms.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Individual results may vary significantly. Please read our <Link to="/terms-of-use" className="text-cyan-600 hover:underline">Terms of Service</Link> for complete details about our services and policies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Boost Your SEO?
          </h2>
          <p className="text-white/90 mb-8">
            Get started with quality backlinks today and improve your search rankings.
          </p>
          <Link to="/#pricing">
            <Button className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8 py-6">
              View Backlink Packages
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} Alignk. All rights reserved.
          </p>
          <div className="mt-4 space-x-4 text-sm">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-gray-400 hover:text-white">Terms of Use</Link>
            <Link to="/refund-policy" className="text-gray-400 hover:text-white">Refund Policy</Link>
            <Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Services;
